import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from '../form/Select';
import Input from '../form/Input';
import { getLeadSourcesRequest } from '../../store/actions/leads';
import { getUsersRequest } from '../../store/actions/users';
import { getContactsRequest } from '../../store/actions/contacts';
import { setJobData, unsetJobFormErrors } from '../../store/actions/jobs';
import AddUserModal from '../users/AddUserModal';
import AddContactModal from '../contacts/AddContactModal';
import AutocompleteInput from '../form/AutocompleteInput';

function LeadCreateRight() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams({});

  const [resPersonValue, setResPersonValue] = useState('');
  const [contactValue, setContactValue] = useState('');

  const pipelinesList = useSelector((state) => state.pipelines.pipelines.job);

  const users = useSelector((state) => state.users.usersList);
  const jobData = useSelector((state) => state.jobs.jobData);
  const errors = useSelector((state) => state.jobs.errors);
  const contactsList = useSelector((state) => state.contacts.contactList);
  const profile = useSelector((state) => state.account.profile);

  const from = searchParams.get('from');

  const pipelines = useMemo(() => {
    if (from === 'archive') {
      return [{ name: 'Archived', status: 'archived' }, ...pipelinesList];
    }
    return pipelinesList;
  }, [pipelinesList, from]);

  const resPersons = useMemo(() => {
    let persons = users.map((u) => ({
      ...u,
      value: u.id,
      label: `${u.firstName} ${u.lastName}`,
    }));
    if (resPersonValue) {
      persons = [...persons, { value: '_create', label: `Create "${resPersonValue}"` }];
    }

    return persons;
  }, [users, resPersonValue]);

  const contacts = useMemo(() => {
    let persons = contactsList.map((u) => ({
      ...u,
      value: u.id,
      label: u.name,
    }));
    if (contactValue) {
      persons = [...persons, { value: '_create', label: `Create "${contactValue}"` }];
    }

    return persons;
  }, [contactsList, contactValue]);

  useEffect(() => {
    const rpId = searchParams.get('rp-id');
    const cId = searchParams.get('c-id');
    if (rpId) {
      (async () => {
        await dispatch(getUsersRequest({ limit: 100 }));
        handleChange('responsiblePerson', +rpId);
      })();
    }
    if (cId) {
      (async () => {
        const { payload: { data } } = await dispatch(getContactsRequest({ limit: 100 }));
        const contact = data.contacts.find((c) => c.id === +cId);
        handleContactChange(contact);
      })();
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(getUsersRequest({ limit: 100 }));
    dispatch(getContactsRequest({ limit: 100 }));
    dispatch(getLeadSourcesRequest({ limit: 100 }));
  }, []);

  const handleChange = useCallback((key, value) => {
    dispatch(setJobData({ key, value }));
    dispatch(unsetJobFormErrors({ key }));
  }, []);

  const handleContactChange = useCallback((val) => {
    const resP = resPersons.find((r) => val?.responsibleUsers?.includes(r.value))?.value;

    handleChange('contact', val?.id?.toString());
    handleChange('phone', val?.phone);
    handleChange('email', val?.email);
    handleChange('address', val?.address || undefined);
    handleChange('company', val?.company || undefined);
    handleChange('responsiblePerson', resP || profile.id);
  }, [profile, resPersons]);

  return (
    <div className="left">
      <p className="title">Job Information</p>
      <Select
        placeholder="Stage"
        options={pipelines}
        isSearchable={false}
        onChange={(val) => handleChange('status', val?.status)}
        value={pipelines.find((s) => s.status === jobData.status)}
        error={errors.status}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => o.status}
        isClearable
        isOptionDisabled={(o) => o.status === 'archived'}
      />
      <Select
        placeholder="Customer name"
        options={contacts}
        onInputChange={(v) => setContactValue(v)}
        onChange={(val) => {
          if (val?.value === '_create') {
            navigate(`?create=contact&from=lead&val=${contactValue}`);
          } else {
            handleContactChange(val);
          }
        }}
        value={contacts.filter((c) => c.id?.toString() === jobData.contact?.toString())}
        error={errors.contact}
        isClearable
      />
      <Input
        placeholder="Company name"
        onChange={(ev) => handleChange('company', ev.target.value || undefined)}
        value={jobData.company}
        error={errors.company}
      />
      <Input
        placeholder="Phone number"
        onChange={(ev) => handleChange('phone', ev.target.value)}
        value={jobData.phone || ''}
        error={errors.phone}
        mask="+1 999-999-9999"
      />
      <Input
        placeholder="Email"
        onChange={(ev) => handleChange('email', ev.target.value)}
        value={jobData.email}
        error={errors.email}
      />
      <AutocompleteInput
        placeholder="Address"
        error={errors.address}
        onSelectOption={(val) => handleChange('address', val || undefined)}
        defaultValue={jobData.address}
      />
      <Input
        placeholder="Sale"
        value={jobData.sale || ''}
        readOnly
      />
      <Select
        placeholder="Responsible person"
        options={resPersons}
        filterOption={(o, val) => o.label.toLowerCase().includes(val.toLowerCase()) || o.value === '_create'}
        onInputChange={(v) => setResPersonValue(v)}
        onChange={(val) => {
          if (val?.value === '_create') {
            navigate('?create=user&from=lead');
          } else {
            handleChange('responsiblePerson', val?.id);
          }
        }}
        value={resPersons.filter((u) => u.id === jobData.responsiblePerson)}
        error={errors.responsiblePerson}
        isClearable
      />

      <AddUserModal />
      <AddContactModal />
    </div>
  );
}

export default LeadCreateRight;
